import { useEffect, useMemo } from 'react';
import {
  HISTORY_DAY_HEADER_MIN_LABEL_WIDTH_PX,
  HISTORY_STATUS_CELL_HORIZONTAL_PADDING_PX,
} from './EntityDailyHistoryPanel.constants';
import { resolveEntityDailyHistoryVisibleDayHeaderIndices } from './EntityDailyHistoryPanel.utils';

type UseEntityDailyHistoryPanelMeasurementsArgs = {
  clampNameColumnWidth: (widthPx: number) => number;
  dayCount: number;
  dayHeaderGridRef: React.RefObject<HTMLDivElement | null>;
  dayHeaderGridWidth: number;
  nameColumnWidthPx: number;
  panelTableWidth: number;
  panelTableContainerRef: React.RefObject<HTMLDivElement | null>;
  setDayHeaderGridWidth: React.Dispatch<React.SetStateAction<number>>;
  setPanelTableWidth: React.Dispatch<React.SetStateAction<number>>;
  setResizedNameColumnWidthPx: React.Dispatch<React.SetStateAction<number | null>>;
};

type UseEntityDailyHistoryPanelMeasurementsResult = {
  visibleDayHeaderIndices: number[];
};

export const useEntityDailyHistoryPanelMeasurements = ({
  clampNameColumnWidth,
  dayCount,
  dayHeaderGridRef,
  dayHeaderGridWidth,
  nameColumnWidthPx,
  panelTableWidth,
  panelTableContainerRef,
  setDayHeaderGridWidth,
  setPanelTableWidth,
  setResizedNameColumnWidthPx,
}: UseEntityDailyHistoryPanelMeasurementsArgs): UseEntityDailyHistoryPanelMeasurementsResult => {
  useEffect(() => {
    const updateDayHeaderGridWidth = (): void => {
      const nextWidth = dayHeaderGridRef.current?.clientWidth ?? 0;
      setDayHeaderGridWidth((currentWidth) =>
        currentWidth === nextWidth ? currentWidth : nextWidth,
      );
    };

    const updatePanelTableWidth = (): void => {
      const nextWidth = panelTableContainerRef.current?.clientWidth ?? 0;
      setPanelTableWidth((currentWidth) => (currentWidth === nextWidth ? currentWidth : nextWidth));
    };

    updateDayHeaderGridWidth();
    updatePanelTableWidth();

    const gridElement = dayHeaderGridRef.current;
    const panelTableContainerElement = panelTableContainerRef.current;
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(() => {
      updateDayHeaderGridWidth();
      updatePanelTableWidth();
      setResizedNameColumnWidthPx((currentWidth) =>
        currentWidth === null ? currentWidth : clampNameColumnWidth(currentWidth),
      );
    });

    if (gridElement) {
      observer.observe(gridElement);
    }
    if (panelTableContainerElement) {
      observer.observe(panelTableContainerElement);
    }

    return () => {
      observer.disconnect();
    };
  }, [
    clampNameColumnWidth,
    dayHeaderGridRef,
    panelTableContainerRef,
    setDayHeaderGridWidth,
    setPanelTableWidth,
    setResizedNameColumnWidthPx,
  ]);

  const derivedStatusAreaWidth = Math.max(
    0,
    panelTableWidth - nameColumnWidthPx - HISTORY_STATUS_CELL_HORIZONTAL_PADDING_PX,
  );
  const effectiveDayHeaderWidth = Math.max(dayHeaderGridWidth, derivedStatusAreaWidth);

  const visibleDayHeaderIndices = useMemo(
    () =>
      resolveEntityDailyHistoryVisibleDayHeaderIndices({
        dayCount,
        gridWidthPx: effectiveDayHeaderWidth,
        minLabelWidthPx: HISTORY_DAY_HEADER_MIN_LABEL_WIDTH_PX,
      }),
    [dayCount, effectiveDayHeaderWidth],
  );

  return { visibleDayHeaderIndices };
};
